import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ShieldCheck, FileBarChart, FileText, ClipboardList, Database, Download, Eye,
  LogOut, Lock, AlertTriangle,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const ITEM_ICON = { REPORT: FileBarChart, DOCUMENT: FileText, QUESTIONNAIRE: ClipboardList,
  ESG_DATASET: Database, SUMMARY: FileText, EVIDENCE: FileText, OTHER: FileText };
const ITEM_LABEL = { REPORT: "Report", DOCUMENT: "Documento", QUESTIONNAIRE: "Questionario",
  ESG_DATASET: "Dati ESG", SUMMARY: "Sintesi", EVIDENCE: "Evidenza", OTHER: "Altro" };

export default function DataRoomPortal() {
  const { token } = useParams();
  const [landing, setLanding] = useState(null);
  const [error, setError] = useState(null);
  const [otp, setOtp] = useState("");
  const [session, setSession] = useState(() => sessionStorage.getItem("drs") || null);
  const [content, setContent] = useState(null);
  const [terms, setTerms] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadContent = useCallback(async (sess) => {
    try {
      const r = await fetch(`${API}/api/data-room/portal/session/${sess}`);
      const data = await r.json();
      if (!r.ok) {
        if (r.status === 401) {
          sessionStorage.removeItem("drs");
          setSession(null);
          setContent(null);
          return;
        }
        throw new Error(data.detail || "Accesso non consentito");
      }
      if (data.terms_required) {
        setTerms(data);
        return;
      }
      setContent(data);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/data-room/portal/${token}`);
        const data = await r.json();
        if (!r.ok) throw new Error(data.detail || "Invito non valido");
        setLanding(data);
      } catch (e) {
        setError(e.message);
      }
    })();
  }, [token]);

  useEffect(() => { if (session) loadContent(session); }, [session, loadContent]);

  const activate = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/data-room/portal/${token}/activate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ otp: otp || null }) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Attivazione non riuscita");
      sessionStorage.setItem("drs", data.session_token);
      setSession(data.session_token);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const acceptTerms = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/data-room/portal/session/${session}/terms`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accept: true }) });
      if (!r.ok) throw new Error("Accettazione non riuscita");
      setTerms(null);
      loadContent(session);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const openItem = async (item, mode) => {
    try {
      const r = await fetch(
        `${API}/api/data-room/portal/session/${session}/items/${item.id}/${mode}`);
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || "Contenuto non disponibile");
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      if (mode === "download") {
        const a = document.createElement("a");
        a.href = url;
        a.download = item.label.replace(/[^\w\d.-]+/g, "_").slice(0, 80);
        a.click();
        URL.revokeObjectURL(url);
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      alert(e.message);
    }
  };

  const logout = async () => {
    if (session) {
      await fetch(`${API}/api/data-room/portal/session/${session}/logout`,
        { method: "POST" }).catch(() => {});
    }
    sessionStorage.removeItem("drs");
    setSession(null);
    setContent(null);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="dataroom-portal">
      <header className="border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center gap-3">
          <img src="/logo-esg-pmi.png" alt="ESG-PMI" className="h-8 w-auto" />
          <div>
            <p className="text-sm font-bold text-slate-900">ESG DATA ROOM</p>
            <p className="text-[11px] text-slate-500">
              Documentazione ESG condivisa in modo controllato{content?.company || landing?.company ? ` per ${content?.company || landing?.company}` : ""}
            </p>
          </div>
          {session && content && (
            <Button size="sm" variant="ghost" className="ml-auto h-8 text-xs"
              onClick={logout} data-testid="portal-logout">
              <LogOut className="w-3 h-3 mr-1" aria-hidden="true" />Esci
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {error && (
          <Card className="border-red-200 bg-red-50" data-testid="portal-error">
            <CardContent className="p-6 text-center">
              <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" aria-hidden="true" />
              <p className="text-sm text-red-800">{error}</p>
            </CardContent>
          </Card>
        )}

        {!error && !session && landing && (
          <Card className="border-slate-200" data-testid="portal-landing">
            <CardContent className="p-8 text-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <Lock className="w-6 h-6 text-green-800" aria-hidden="true" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">{landing.room.name}</h1>
                <p className="text-sm text-slate-600 mt-1">
                  Condivisa per {landing.recipient.organization_name} · Accesso valido fino
                  al {new Date(landing.room.expires_at).toLocaleDateString("it-IT")}
                </p>
              </div>
              {landing.room.access_mode === "MAGIC_LINK_OTP" && (
                <div className="max-w-xs mx-auto">
                  <label className="text-xs text-slate-600" htmlFor="otp-input">
                    Codice di verifica (comunicato dal consulente)
                  </label>
                  <input id="otp-input" type="text" inputMode="numeric" maxLength={6}
                    value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    className="mt-1 w-full h-10 rounded-md border border-slate-300 px-3 text-center text-lg tracking-[0.3em] font-mono"
                    data-testid="portal-otp-input" autoComplete="one-time-code" />
                </div>
              )}
              <Button
                className="bg-green-700 hover:bg-green-800 text-white"
                disabled={busy || (landing.room.access_mode === "MAGIC_LINK_OTP" && otp.length !== 6)}
                onClick={activate} data-testid="portal-activate-btn">
                {busy ? "Verifica in corso…" : "Accedi alla Data Room"}
              </Button>
              <p className="text-[11px] text-slate-400">{landing.notice}</p>
            </CardContent>
          </Card>
        )}

        {session && terms && (
          <Card className="border-slate-200" data-testid="portal-terms">
            <CardContent className="p-8 space-y-4">
              <h2 className="text-lg font-bold text-slate-900">
                Termini di riservatezza ({terms.terms_version})
              </h2>
              <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed max-w-prose">
                {terms.terms_text}
              </p>
              <Button className="bg-green-700 hover:bg-green-800 text-white"
                disabled={busy} onClick={acceptTerms} data-testid="portal-accept-terms">
                Accetto i termini e accedo
              </Button>
            </CardContent>
          </Card>
        )}

        {session && content && !terms && (
          <div className="space-y-6" data-testid="portal-content">
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
              <div className="flex items-center gap-2 flex-wrap">
                <ShieldCheck className="w-4 h-4 text-green-700" aria-hidden="true" />
                <p className="text-sm font-semibold text-slate-900">{content.room.name}</p>
                {content.room.confidentiality_level !== "STANDARD" && (
                  <Badge className="bg-amber-100 text-amber-800" data-testid="portal-confidentiality">
                    {content.room.confidentiality_level}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Accesso personale per {content.recipient.organization_name} · Scade il{" "}
                {new Date(content.room.expires_at).toLocaleDateString("it-IT")} · Sessione fino
                alle {new Date(content.session_expires_at).toLocaleTimeString("it-IT")}
              </p>
              <p className="text-[11px] text-slate-400 mt-1">{content.notice}</p>
            </div>

            {content.score_disclaimer && (
              <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2"
                data-testid="portal-score-disclaimer">
                {content.score_disclaimer}
              </p>
            )}

            <div className="space-y-2">
              {content.items.length === 0 && (
                <p className="text-sm text-slate-500">Nessun contenuto disponibile per te.</p>
              )}
              {content.items.map((item) => {
                const Icon = ITEM_ICON[item.item_type] || FileText;
                return (
                  <Card key={item.id} className="border-slate-200"
                    data-testid={`portal-item-${item.id}`}>
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-sky-50 text-sky-700">
                        <Icon className="w-4 h-4" aria-hidden="true" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900">{item.label}</p>
                        <p className="text-[11px] text-slate-500">
                          {ITEM_LABEL[item.item_type]} · v{item.content_version}
                        </p>
                      </div>
                      <Button size="sm" variant="outline" className="h-8 text-xs"
                        onClick={() => openItem(item, "view")}
                        data-testid={`portal-view-${item.id}`}>
                        <Eye className="w-3 h-3 mr-1" aria-hidden="true" />Apri
                      </Button>
                      {item.permission === "DOWNLOAD" ? (
                        <Button size="sm"
                          className="h-8 text-xs bg-green-700 hover:bg-green-800 text-white"
                          onClick={() => openItem(item, "download")}
                          data-testid={`portal-download-${item.id}`}>
                          <Download className="w-3 h-3 mr-1" aria-hidden="true" />Scarica
                        </Button>
                      ) : (
                        <span className="text-[10px] text-slate-400"
                          data-testid={`portal-nodownload-${item.id}`}>
                          Solo visualizzazione
                        </span>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <p className="text-[11px] text-slate-400 text-center">
              Ogni visualizzazione e download è registrata. Documento riservato:
              l'accesso è personale.
            </p>
          </div>
        )}
      </main>

      <footer className="border-t border-slate-100 mt-10">
        <p className="max-w-3xl mx-auto px-6 py-4 text-[11px] text-slate-400">
          ESG-PMI · IL PIANETA SEI TU — Data Room sicura. Accesso tracciato e revocabile.
        </p>
      </footer>
    </div>
  );
}
